import React from 'react';
import { OptionProps, components, ControlProps } from 'react-select';

import { Box, Text, Select } from '@lucidhq/lucidium';
import { BackgroundColorProperty } from '@lucidhq/lucidium/lss/csstypes';

import { TemplateQuestion } from '~/components/Shared/hooks/useFetchTemplateQuestions';
import {
  EnhancedTemplateQuestion,
  AnswerOption,
  NOTA
} from '~/utils/templateQuestions';

import { useCampaignManager } from '../../CampaignManagerProvider';
import { SurveyStepIds } from '../../useCampaignManagerSteps';
import { useBehavioralQuestions } from '../BehavioralQuestionsStep/BehavioralQuestionsProvider';
import { useStandardQuestionsProvider } from '../StandardQuestionsStep/StandardQuestionsProvider';

type QuestionTypeSelectProps = {
  isReadOnly: boolean;
  question: EnhancedTemplateQuestion;
  onSave: (data: Partial<TemplateQuestion>) => void;
};

const TEXT = 'text';

const getAnswersFromContext = (question: EnhancedTemplateQuestion) => {
  const selectedAnswers = !!question.use_answer_meta
    ? question.answers
    : (question?.customAnswers as string[]);

  const notaAnswer = question?.none_of_the_above_answer ?? '';

  const answers =
    !selectedAnswers.includes(notaAnswer) && !!notaAnswer?.length
      ? [...selectedAnswers, notaAnswer]
      : selectedAnswers;

  const filteredAnswers = answers.filter(answer => answer !== null);

  return filteredAnswers;
};

type GetUniqueAnswers = {
  answers?: string[];
  notaAnswer: string;
  question: EnhancedTemplateQuestion;
};

const getUniqueAnswers = ({
  answers,
  notaAnswer,
  question
}: GetUniqueAnswers) => {
  const updatedAnswers =
    !answers?.includes(notaAnswer) && !!question?.none_of_the_above_answer
      ? [...(answers as string[]), notaAnswer]
      : answers;

  const filteredAnswers = updatedAnswers?.filter(answer => answer !== null);

  return filteredAnswers;
};

export const QuestionTypeSelect = ({
  isReadOnly,
  question,
  onSave
}: QuestionTypeSelectProps) => {
  const { options: stepOptions } = useCampaignManager();
  const { translatedNOTAValue = '' } = useStandardQuestionsProvider();
  const { referenceNOTAValue = '' } = useBehavioralQuestions();

  const isStandard =
    stepOptions?.activeChildStep === SurveyStepIds.STANDARD_QUESTIONS;

  const currentNOTAValue = isStandard
    ? translatedNOTAValue
    : referenceNOTAValue;

  return (
    <Select
      labelKey="label"
      valueKey="value"
      options={question.meta.allowedQuestionTypeOptions}
      value={question.question_type}
      onChange={(value: any) => {
        const action = { question_type: value };
        const isGridType = value.includes('table');
        const notaAnswer = currentNOTAValue ?? NOTA;
        const isCustomText =
          value.includes(TEXT) && !!question.meta.isCustomKPI;
        //NOTE this is for both single and multi choice questions
        const isCustomNonGrid =
          !value.includes(TEXT) && !!question.meta.isCustomKPI && !isGridType;
        const isCustomGrid = isGridType && !!question.meta.isCustomKPI;

        if (isCustomText) {
          action['randomize'] = false;
          action['none_of_the_above_answer'] = null;
        } else if (isCustomNonGrid) {
          action['randomize'] = true;
          action['none_of_the_above_answer'] =
            question?.none_of_the_above_answer;
          action['answers'] = getAnswersFromContext(question);
          action['customAnswers'] = getUniqueAnswers({
            answers: question?.customAnswers,
            notaAnswer,
            question
          });
          action['defaultBrandAnswers'] = getUniqueAnswers({
            answers: question?.defaultBrandAnswers,
            notaAnswer,
            question
          });
        } else if (isCustomGrid) {
          action['randomize'] = true;
          action['none_of_the_above_answer'] =
            question?.none_of_the_above_answer;
          // NOTE: due to an extreme edge case it is vital for custom grid questions to have a default answers array due to API serializer validation.
          action['answers'] = getUniqueAnswers({
            answers: question?.defaultBrandAnswers,
            notaAnswer,
            question
          });
        }

        onSave(action);
      }}
      disabled={isReadOnly}
      styles={{
        control: (baseStyles: any) => ({
          ...baseStyles,
          backgroundColor: 'hsl(0, 0%, 100%)',
          color: 'hsl(0, 0%, 46%)'
        })
      }}
      components={{
        Control: (props: ControlProps<AnswerOption>) => (
          <Box
            lss={{
              backgroundColor: 'hsl(0, 0%, 100%)' as BackgroundColorProperty
            }}
          >
            <components.Control {...props} />
          </Box>
        ),
        Option: (props: OptionProps<AnswerOption>) => (
          <Select.Option {...props}>
            <Text>{props.children}</Text>
          </Select.Option>
        ),
        SingleValue: (props: OptionProps<AnswerOption>) => (
          <Text lss={{ color: isReadOnly ? 'gray700' : 'secondary500' }}>
            {props.children}
          </Text>
        )
      }}
    />
  );
};
